// Entropy estimation for generated secrets. Because we generate uniformly from a
// known pool, entropy is exactly length * log2(poolSize) — far more honest than a
// heuristic like zxcvbn for *generated* (non-human) secrets (see plan, KISS).

export type StrengthLevel = 'weak' | 'fair' | 'strong' | 'excellent';

export interface Strength {
  bits: number;
  level: StrengthLevel;
  /** 0..1 for a progress bar. */
  fraction: number;
  /** Brand color token name (without var()). */
  colorToken: string;
}

/** Entropy in bits for a random character password. */
export function passwordEntropyBits(length: number, pool: number): number {
  if (length <= 0 || pool <= 1) return 0;
  return length * Math.log2(pool);
}

/** Entropy in bits for a passphrase of `wordCount` words drawn from a list. */
export function passphraseEntropyBits(
  wordCount: number,
  listSize: number,
  includeNumber: boolean,
): number {
  if (wordCount <= 0 || listSize <= 1) return 0;
  const base = wordCount * Math.log2(listSize);
  return base + (includeNumber ? Math.log2(10) : 0);
}

/**
 * Entropy in bits for a memorable password: words plus the digits+symbol+digits
 * tail (10–99 = 90 options, a symbol, 1000–9999 = 9000 options).
 */
export function memorableEntropyBits(
  wordCount: number,
  listSize: number,
  symbolCount: number,
): number {
  if (wordCount <= 0 || listSize <= 1) return 0;
  const words = wordCount * Math.log2(listSize);
  const tail = Math.log2(90) + Math.log2(symbolCount) + Math.log2(9000);
  return words + tail;
}

/**
 * Maps entropy bits to a qualitative strength. Thresholds follow common guidance:
 * <40 weak, 40-59 fair, 60-79 strong, >=80 excellent.
 */
export function classifyStrength(bits: number): Strength {
  let level: StrengthLevel;
  let colorToken: string;

  if (bits < 40) {
    level = 'weak';
    colorToken = '--error';
  } else if (bits < 60) {
    level = 'fair';
    colorToken = '--warning';
  } else if (bits < 80) {
    level = 'strong';
    colorToken = '--primary';
  } else {
    level = 'excellent';
    colorToken = '--success';
  }

  // Cap the bar at 128 bits so very long secrets do not all read identically.
  const fraction = Math.min(1, bits / 128);
  return { bits, level, fraction, colorToken };
}
